import {
  IProcedureCopayListItem,
  IProcedureCopayListItemSaveRequest,
  IProcedureCopayListResolution,
  IProcedureCopayListResolveProcedure,
  ProcedureCopayListCodeStatus,
} from "types/ProcedureCopayList/ProcedureCopayList";

/**
 * CLE-2816 PR 2 — the words and checks of Eligibility › Copay list, in one place so the table, the
 * dialog and the tester say the same thing. The dialog's checks are the API's 400s / 409s, made
 * before anything is sent.
 */

export const COPAY_LIST_INTRO =
  "Some UHC plans answer a facility care setting with a list of copays, one per kind of procedure, each marked “AMOUNT VARIES BY LOCATION” and each with a lower and an upper amount. This list says which item on the payer's list each CPT / HCPCS code is priced under. Every hospital uses the same list, and only the benefit engine reads it.";

export const COPAY_LIST_RULES = [
  "A code mapped to an item is quoted at the item's higher (M2) amount.",
  "A code that is not on this list uses the payer's “OUTPATIENT HOSPITAL CARE” item at its lower (8H) amount, when no other code on the estimate is mapped.",
  "When an estimate has several procedures, the item whose codes carry the largest total facility charge decides. Add-on codes never decide.",
];

export const ITEM_NAME_HELP =
  "Exactly as the payer's line spells it — capitals, spaces and punctuation included — or the engine will not find it.";

export const ADD_ON_LABEL = "Add-on code — never decides the item";

export const NOTE_MAX_LENGTH = 200;
export const ITEM_NAME_MAX_LENGTH = 200;

/** Five digits, or a HCPCS letter and four digits — the API's `PROCEDURE_CODE_SHAPE`. */
const CODE_SHAPE = /^(?:\d{5}|[A-Z]\d{4})$/;

export const normalizeCode = (code: string): string =>
  (code ?? "").trim().toUpperCase();

export interface ICopayListDraft {
  cptCode: string;
  itemName: string;
  addOn: boolean;
  note: string;
}

export const toCopayListDraft = (
  row: IProcedureCopayListItem | null
): ICopayListDraft => ({
  cptCode: row?.cptCode ?? "",
  itemName: row?.itemName ?? "",
  addOn: row ? row.itemName === null : false,
  note: row?.note ?? "",
});

/** What is wrong with the draft, in words, or null. `editingCode`: the row's own code. */
export const copayListDraftProblem = (
  draft: ICopayListDraft,
  existing: IProcedureCopayListItem[],
  editingCode: string | null
): string | null => {
  const cptCode = normalizeCode(draft.cptCode);
  if (!CODE_SHAPE.test(cptCode)) {
    return "The code must be 5 characters: five digits, or a HCPCS letter and four digits. Leave off any modifier.";
  }
  const itemName = draft.itemName.trim();
  if (!draft.addOn && !itemName) {
    return "Type the item, or tick “Add-on code” if this code never decides the item.";
  }
  if (!draft.addOn && itemName.length > ITEM_NAME_MAX_LENGTH) {
    return `Keep the item under ${ITEM_NAME_MAX_LENGTH} characters.`;
  }
  if (draft.note.trim().length > NOTE_MAX_LENGTH) {
    return `Keep the note under ${NOTE_MAX_LENGTH} characters.`;
  }
  if (
    cptCode !== editingCode &&
    existing.some((row) => row.cptCode === cptCode)
  ) {
    return `${cptCode} is already on the list. Edit that row instead.`;
  }
  return null;
};

export const copayListToSaveRequest = (
  draft: ICopayListDraft
): IProcedureCopayListItemSaveRequest => ({
  cptCode: normalizeCode(draft.cptCode),
  itemName: draft.addOn ? null : draft.itemName.trim() || null,
  note: draft.note.trim() || null,
});

const squash = (text: string) => text.replace(/\s+/g, " ").trim().toUpperCase();

/**
 * A warning — never a block — when the typed item is not a name already in use. The engine
 * compares it byte for byte with the payer's line, so a near miss quietly matches nothing.
 */
export const unknownItemWarning = (
  itemName: string,
  addOn: boolean,
  itemNames: string[]
): string | null => {
  const typed = itemName.trim();
  if (addOn || !typed || itemNames.includes(typed)) return null;
  const near = itemNames.find((name) => squash(name) === squash(typed));
  return `“${typed}” is not an item name already in use. Check it against the payer's line, character for character.${
    near ? ` Did you mean “${near}”?` : ""
  }`;
};

const codesList = (codes: string[]) => codes.join(", ");

/** Why the tester's first item is the one quoted. */
export const resolveReasonInWords = ({
  reason,
  quoted,
  decidingCodes,
}: Pick<
  IProcedureCopayListResolution,
  "reason" | "quoted" | "decidingCodes"
>): string => {
  switch (reason) {
    case "NOTHING_MAPPED":
      return `No code is mapped to an item, so the generic item ${quoted} is quoted.`;
    case "ONE_ITEM":
      return `${quoted} is quoted: it is the only item the codes map to (${codesList(
        decidingCodes
      )}).`;
    case "LARGEST_FACILITY_CHARGE":
      return `${quoted} is quoted: its codes (${codesList(
        decidingCodes
      )}) carry the largest facility charge.`;
    case "REQUEST_ORDER_TIE":
      return `${quoted} is quoted: the facility charges are a tie, so the item of the first code typed (${codesList(
        decidingCodes
      )}) wins.`;
    case "REQUEST_ORDER_NO_AMOUNTS":
    default:
      return `${quoted} is quoted: no facility charges were given, so the item of the first mapped code typed (${codesList(
        decidingCodes
      )}) wins.`;
  }
};

export const codeStatusInWords = (
  status: ProcedureCopayListCodeStatus,
  itemName: string | null
): string => {
  switch (status) {
    case "MAPPED":
      return itemName ?? "";
    case "ADD_ON":
      return "Add-on — never decides the item";
    case "UNMAPPED":
      return "Not on the list";
    case "NOT_A_CODE":
    default:
      return "Not a CPT / HCPCS code";
  }
};

/** Typed dollars → whole cents. Blank → undefined (no amount); unreadable or negative → null. */
export const dollarsToCents = (text: string): number | null | undefined => {
  const cleaned = (text ?? "").replace(/[$,\s]/g, "");
  if (!cleaned) return undefined;
  if (!/^\d+(?:\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
};

export const centsInDollars = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/** The tester's text: one procedure per line, a code then optionally its facility charge. */
export const proceduresFromText = (
  text: string
): {
  procedures: IProcedureCopayListResolveProcedure[];
  problem: string | null;
} => {
  const procedures: IProcedureCopayListResolveProcedure[] = [];
  let problem: string | null = null;
  (text ?? "")
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const [code, ...rest] = line.split(/[\s,;]+/);
      const amount = dollarsToCents(rest.join(""));
      if (amount === null) {
        problem =
          problem ?? `${code}: the facility charge must be a dollar amount.`;
        return;
      }
      procedures.push(
        amount === undefined ? { code } : { code, amountInCents: amount }
      );
    });
  return { procedures, problem };
};
